import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenu,
  SidebarRail,
  SidebarGroupLabel,
  SidebarGroupContent,
  useSidebar,
  SidebarFooter,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router-dom";
import { UserNav } from "./UserNav";
import { useAuthInfo } from "@propelauth/react";
import { navigationConfig, NavigationItem } from "@/lib/navigation";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Folder, FolderOpen } from "lucide-react";
import {
  getOrganizationName,
  hasRequiredRoles,
  hasRequiredPermissions,
} from "@/utils/authUtils";
import { getSandboxSkills } from "@/utils/apiCalls";
import { SandboxSkill } from "@/types";

const SidebarItem = (props: {
  icon: React.ReactNode;
  label: string;
  href: string;
}) => {
  const { pathname } = useLocation();
  const isActive = pathname === props.href;

  return (
    <SidebarMenuItem key={props.label}>
      <SidebarMenuButton asChild tooltip={props.label}>
        <a
          href={props.href}
          className={cn(
            "flex items-center gap-2",
            isActive && "bg-primary text-primary-foreground"
          )}
        >
          {props.icon}
          <span>{props.label}</span>
        </a>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const skillKey = (skill: SandboxSkill) =>
  [
    skill.name,
    skill.scope,
    skill.specialty ?? "",
    skill.output_document_type_id ?? "",
    skill.section_id ?? "",
    skill.clinician_id ?? "",
  ].join(":");

const latestSkillVersions = (skills: SandboxSkill[]) => {
  const latest = new Map<string, SandboxSkill>();
  for (const skill of skills) {
    const key = skillKey(skill);
    const current = latest.get(key);
    if (!current || skill.version > current.version) {
      latest.set(key, skill);
    }
  }
  return Array.from(latest.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

const formatSkillName = (skill: SandboxSkill) =>
  skill.name.replace(/[_-]/g, " ");

const SandboxSkillsSidebarItem = () => {
  const { pathname } = useLocation();
  const authInfo = useAuthInfo();
  const [isOpen, setIsOpen] = useState(
    pathname.startsWith("/admin/sandbox-config")
  );
  const [skills, setSkills] = useState<SandboxSkill[]>([]);

  useEffect(() => {
    if (!hasRequiredRoles(authInfo, ["Admin"])) {
      return;
    }
    const loadSkills = async () => {
      const response = await getSandboxSkills(authInfo.accessToken ?? null);
      setSkills(response ?? []);
    };
    loadSkills();
  }, [authInfo.accessToken]);

  if (!hasRequiredRoles(authInfo, ["Admin"])) {
    return null;
  }

  const visibleSkills = latestSkillVersions(skills);
  const isActive = pathname.startsWith("/admin/sandbox-config");

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        type="button"
        tooltip="Skills"
        isActive={isActive}
        onClick={() => setIsOpen((current) => !current)}
      >
        {isOpen ? <FolderOpen size={20} /> : <Folder size={20} />}
        <span>Skills</span>
        {isOpen ? (
          <ChevronDown className="ml-auto" size={16} />
        ) : (
          <ChevronRight className="ml-auto" size={16} />
        )}
      </SidebarMenuButton>
      {isOpen && (
        <SidebarMenuSub>
          {visibleSkills.length === 0 ? (
            <SidebarMenuSubItem>
              <span className="block px-2 py-1 text-xs text-muted-foreground">
                No skills yet
              </span>
            </SidebarMenuSubItem>
          ) : (
            visibleSkills.map((skill) => (
              <SidebarMenuSubItem key={skill.id}>
                <SidebarMenuSubButton
                  href={`/admin/sandbox-config/${skill.id}`}
                  isActive={pathname === `/admin/sandbox-config/${skill.id}`}
                >
                  <span className="capitalize">{formatSkillName(skill)}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))
          )}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
};

const NavigationGroup = ({
  label,
  items,
  requiredRoles,
  requiredPermissions,
  collapsible = false,
}: {
  label: string;
  items: NavigationItem[];
  requiredRoles?: string[];
  requiredPermissions?: string[];
  collapsible?: boolean;
}) => {
  const authInfo = useAuthInfo();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Check if user has required roles and permissions for the group
  if (
    (requiredRoles && !hasRequiredRoles(authInfo, requiredRoles)) ||
    (requiredPermissions &&
      !hasRequiredPermissions(authInfo, requiredPermissions))
  ) {
    return null;
  }

  const filteredItems = items.filter(
    (item) =>
      (!item.requiredRoles || hasRequiredRoles(authInfo, item.requiredRoles)) &&
      (!item.requiredPermissions ||
        hasRequiredPermissions(authInfo, item.requiredPermissions))
  );

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel
        className={cn(
          "flex items-center justify-between",
          collapsible && "cursor-pointer"
        )}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        <span>{label}</span>
        {collapsible && (
          <span className="ml-2">
            {isCollapsed ? (
              <ChevronRight size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </span>
        )}
      </SidebarGroupLabel>
      {!isCollapsed && (
        <SidebarGroupContent>
          <SidebarMenu>
            {filteredItems.map((item) => (
              <SidebarItem
                key={item.label}
                icon={<item.icon size={20} />}
                label={item.label}
                href={item.href}
              />
            ))}
            {label === "Admin Settings" && <SandboxSkillsSidebarItem />}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
};

export const PageSidebar = () => {
  const { isMobile, state } = useSidebar();
  const authInfo = useAuthInfo();
  const organizationName = getOrganizationName(authInfo);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Logo" className="h-auto w-10" />
              <div className="flex flex-col">
                {!isMobile && state !== "collapsed" && (
                  <span className="text-lg font-semibold">Mdley</span>
                )}
                {!isMobile && state !== "collapsed" && organizationName && (
                  <span className="text-sm text-muted-foreground">
                    {organizationName}
                  </span>
                )}
              </div>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {navigationConfig.map((group) => (
          <NavigationGroup key={group.label} {...group} />
        ))}
      </SidebarContent>
      <SidebarFooter>
        <UserNav />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
